import styled from "styled-components";
import AngleUp from "assets/icons/angle-up.svg";
import DropdownCustom from "components/global/DropdownCustom";
import check from "assets/icons/check_secondary.svg";
import { useState } from "react";
import Loader from "components/global/Loader";
import Spacer from "components/global/Spacer";
import moment from "moment";
import { voteFeature } from "api";

const Wrapper = styled.div`
  height: 100%;
  overflow: auto;

  .empty-state {
    max-width: 305px;
    header {
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
      margin-bottom: 8px;
    }

    .description {
      font-size: 14px;
      font-weight: 450;
      line-height: 20px;
      color: var(--body_text);
    }
  }

  .filter-container {
    height: 56px;
    padding: 24px;
  }

  .select-category {
    width: 160px;
    height: 36px;

    input,
    .inputWrapper {
      font-size: 14px;
      font-weight: 450;
    }

    .listItem {
      font-size: 14px;
      font-weight: 450;
    }
  }

  .new {
    color: var(--secondary);
    background: #f1f9ff;
  }

  .review {
    background: #e7ebf9;
    color: #5114ff;
  }

  .planned {
    background: #fbfbef;
    color: #c48100;
  }

  .dev {
    color: #b75801;
    background-color: #fbfbef;
  }

  .shipped {
    background-color: #e9f9e7;
    color: #027a48;
  }
`;

const FeatureItem = styled.div`
  border-bottom: 1px solid var(--grey_10);
  padding: 24px;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;

  .content-container {
    .title {
      font-size: 16px;
      font-weight: 450;
      line-height: 20px;
      margin-bottom: 8px;
    }

    .description {
      font-size: 14px;
      color: var(--grey_6);
      font-weight: 450;
      line-height: 20px;
    }
  }

  .vote-btn {
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 450;
    line-height: 24px;
    border-radius: 8px;
    border: 1px solid var(--grey_10);
    background: var(--background);
    width: 60px;
    min-width: 60px;
    height: 68px;
    overflow: hidden;
  }

  .has-vote {
    background-color: #f7fafd;
    color: var(--secondary);
    pointer-events: none;
  }

  .info-flex {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    font-size: 14px;
  }

  .name {
    font-weight: 500;
    color: var(--grey_7);
  }

  .time {
    font-weight: 450;
    color: var(--grey_8);
  }

  .divider {
    background: #e2e2ea;
    width: 1px;
    height: 13px;
  }

  .tag {
    padding: 2px 8px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: capitalize;
    font-size: 12px;
  }
`;

const FilterDropdown = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .color {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }

  .new {
    background: var(--secondary);
  }

  .in_review {
    background: #5114ff;
  }

  .planned {
    background: #c48100;
  }

  .in_development {
    background: #b75801;
  }

  .shipped {
    background: #027a48;
  }
`;

const renderFilterComponent = (cat) => {
  const colorType = cat.toLowerCase().replace(/ /g, "_");
  return (
    <FilterDropdown className="renderFilter">
      <div className={`color ${colorType}`}></div>
      <div>{cat}</div>
    </FilterDropdown>
  );
};

const Feature = ({ feature, index, list, lastListElementRef }) => {
  const [voteCount, setVoteCount] = useState(feature.vote);
  const [isChecked, setIsChecked] = useState(feature.has_voted);

  const vote = async () => {
    if (voteCount > feature.vote) return;

    setVoteCount((prev) => prev + 1);
    setIsChecked(true);
    try {
      await voteFeature(feature.id, "upvote");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <FeatureItem ref={list?.length === index + 1 ? lastListElementRef : null}>
      <div className="content-container">
        <p className="title">{feature?.feature_name}</p>
        <p className="description">{feature?.description}</p>

        <div className="info-flex">
          <div className="name">{feature?.user?.full_name}</div>
          <div className="divider" />
          <div className="time">
            {moment(new Date(feature.created_at)).fromNow()}
          </div>
          <div className="divider" />
          <div className={`tag ${feature.tag}`}>{feature.tag}</div>
        </div>
      </div>
      <button className={`vote-btn ${isChecked ? "has-vote" : ""}`} onClick={vote}>
        {isChecked ? (
          <img src={check} alt="check" width="16px" />
        ) : (
          <img src={AngleUp} alt="upvote" />
        )}
        {voteCount}
      </button>
    </FeatureItem>
  );
};

const Features = ({
  featureList,
  setFeatureList,
  loading,
  handleFilterCategory,
  category,
  categories,
  lastListElementRef,
}) => {
  return (
    <Wrapper>
      <div className="filter-container flexRow alignCenter">
        <DropdownCustom
          className="select-category"
          name="select-category"
          list={categories}
          value={category}
          setValue={(value) => handleFilterCategory(value)}
          renderContent={renderFilterComponent}
        />
      </div>
      {!featureList.length && !loading && (
        <div className="empty-state centralize textCenter">
          <header>Got some ideas?</header>
          <p className="description">
            It seems this Suggestion Board hasn't received any submissions yet.
            You can be the first to contribute. Share your ideas now!
          </p>
        </div>
      )}

      {!!featureList.length &&
        featureList?.map((feature, idx) => (
          <Feature
            key={feature.id}
            feature={feature}
            lastListElementRef={lastListElementRef}
            index={idx}
            list={featureList}
          />
        ))}

      <Spacer y={3} />
      {loading && <Loader />}
    </Wrapper>
  );
};

export default Features;
